/**
 * DEVICE identity of Enviar's optional account.
 *
 * An account has no email or password: it is a list of trusted
 * devices, and each device IS a key pair generated locally:
 *
 *  - **Signing (ECDSA P-256):** authenticates requests to the mailbox (headers
 *    `x-enviar-device`/`x-enviar-ts`/`x-enviar-sig`). The server only knows
 *    the PUBLIC key; the private one never leaves the device.
 *  - **Encryption (ECDH P-256):** senders derive the envelope's AES key with it
 *    (see `envelope.ts`). The private one does not leave the device either.
 *
 * The fingerprint (`deviceId`) is sha256(signing public key)[0..16] in hex — the
 * SAME derivation the server does (parity pinned with a shared vector
 * in the TS and Rust tests).
 */

import { isRecord } from "../p2p/guards";
import { sha256Hex } from "../transfer/hash";
import { fromBase64Url, toArrayBuffer, toBase64Url, utf8Decode, utf8Encode } from "./encoding";

/** Prefix of the signed canonical string — MUST match `mailbox.rs`. */
const CANONICAL_PREFIX = "ENVIAR-MAILBOX-V1";

const ECDSA_PARAMS: EcKeyGenParams = { name: "ECDSA", namedCurve: "P-256" };
const ECDH_PARAMS: EcKeyGenParams = { name: "ECDH", namedCurve: "P-256" };

export interface DeviceIdentity {
  /** Public fingerprint of the device (id before the server). */
  deviceId: string;
  /** Signing public key (uncompressed SEC1, base64url). */
  sigPub: string;
  /** Encryption public key (uncompressed SEC1, base64url). */
  encPub: string;
  sigPrivateKey: CryptoKey;
  encPrivateKey: CryptoKey;
}

/** Serializable form (JWK) to persist the identity on the device. */
export interface StoredDeviceIdentity {
  v: 1;
  sigPub: string;
  encPub: string;
  sigPrivateJwk: JsonWebKey;
  encPrivateJwk: JsonWebKey;
}

function subtle(): SubtleCrypto {
  const cryptoObj = globalThis.crypto;
  if (!cryptoObj?.subtle) {
    throw new Error("WebCrypto SubtleCrypto is not available in this environment");
  }
  return cryptoObj.subtle;
}

/** Device fingerprint: sha256(SEC1 bytes of the signing key)[0..16]. */
export async function deviceFingerprint(sigPubB64u: string): Promise<string> {
  const hex = await sha256Hex(fromBase64Url(sigPubB64u));
  return hex.slice(0, 16);
}

/** Generates a new device identity (extractable keys so they can be persisted). */
export async function generateDeviceIdentity(): Promise<DeviceIdentity> {
  const s = subtle();
  const sig = await s.generateKey(ECDSA_PARAMS, true, ["sign", "verify"]);
  const enc = await s.generateKey(ECDH_PARAMS, true, ["deriveBits"]);
  const sigPub = toBase64Url(new Uint8Array(await s.exportKey("raw", sig.publicKey)));
  const encPub = toBase64Url(new Uint8Array(await s.exportKey("raw", enc.publicKey)));
  return {
    deviceId: await deviceFingerprint(sigPub),
    sigPub,
    encPub,
    sigPrivateKey: sig.privateKey,
    encPrivateKey: enc.privateKey,
  };
}

/** Serializes the identity to store it (e.g. in `localStorage`). */
export async function exportDeviceIdentity(
  identity: DeviceIdentity,
): Promise<StoredDeviceIdentity> {
  const s = subtle();
  return {
    v: 1,
    sigPub: identity.sigPub,
    encPub: identity.encPub,
    sigPrivateJwk: await s.exportKey("jwk", identity.sigPrivateKey),
    encPrivateJwk: await s.exportKey("jwk", identity.encPrivateKey),
  };
}

/** Rehydrates a persisted identity. Throws if the material is not valid. */
export async function importDeviceIdentity(stored: StoredDeviceIdentity): Promise<DeviceIdentity> {
  const s = subtle();
  const sigPrivateKey = await s.importKey("jwk", stored.sigPrivateJwk, ECDSA_PARAMS, true, [
    "sign",
  ]);
  const encPrivateKey = await s.importKey("jwk", stored.encPrivateJwk, ECDH_PARAMS, true, [
    "deriveBits",
  ]);
  return {
    deviceId: await deviceFingerprint(stored.sigPub),
    sigPub: stored.sigPub,
    encPub: stored.encPub,
    sigPrivateKey,
    encPrivateKey,
  };
}

/** Is a `StoredDeviceIdentity` plausible? (structural validation on load). */
export function isStoredDeviceIdentity(value: unknown): value is StoredDeviceIdentity {
  return (
    isRecord(value) &&
    value.v === 1 &&
    typeof value.sigPub === "string" &&
    typeof value.encPub === "string" &&
    isRecord(value.sigPrivateJwk) &&
    isRecord(value.encPrivateJwk)
  );
}

/**
 * Signs the mailbox canonical request — SAME format the server verifies:
 * `ENVIAR-MAILBOX-V1\n{METHOD}\n{path}\n{ts}\n{sha256(body) hex}`.
 * Returns the raw ECDSA r||s signature (64 bytes) in base64url.
 */
export async function signMailboxRequest(
  sigPrivateKey: CryptoKey,
  options: { method: string; path: string; tsSecs: number; body?: Uint8Array },
): Promise<string> {
  const bodyHash = await sha256Hex(options.body ?? new Uint8Array(0));
  const message = [
    CANONICAL_PREFIX,
    options.method.toUpperCase(),
    options.path,
    String(options.tsSecs),
    bodyHash,
  ].join("\n");
  const signature = await subtle().sign(
    { name: "ECDSA", hash: "SHA-256" },
    sigPrivateKey,
    toArrayBuffer(utf8Encode(message)),
  );
  return toBase64Url(new Uint8Array(signature));
}

// ─── Device link code ────────────────────────────────────────────────────────
// To add a NEW device to an account: the new one generates its identity
// and shows this code; an ALREADY trusted device pastes it and authorizes the
// enrollment with its signature. Only PUBLIC material travels (public keys + name).

export const DEVICE_LINK_CODE_PREFIX = "ENVDEV1-";

export interface DeviceLinkPayload {
  sigPub: string;
  encPub: string;
  name: string;
}

export function buildDeviceLinkCode(payload: DeviceLinkPayload): string {
  const json = JSON.stringify({ v: 1, ...payload });
  return `${DEVICE_LINK_CODE_PREFIX}${toBase64Url(utf8Encode(json))}`;
}

/** `null` if the code is not a valid device link. */
export function parseDeviceLinkCode(raw: string): DeviceLinkPayload | null {
  const trimmed = raw.trim();
  if (!trimmed.toUpperCase().startsWith(DEVICE_LINK_CODE_PREFIX)) {
    return null;
  }
  try {
    const json = utf8Decode(fromBase64Url(trimmed.slice(DEVICE_LINK_CODE_PREFIX.length)));
    const value: unknown = JSON.parse(json);
    if (
      !isRecord(value) ||
      value.v !== 1 ||
      typeof value.sigPub !== "string" ||
      typeof value.encPub !== "string" ||
      typeof value.name !== "string" ||
      value.sigPub === "" ||
      value.encPub === "" ||
      value.name.trim() === ""
    ) {
      return null;
    }
    return { sigPub: value.sigPub, encPub: value.encPub, name: value.name };
  } catch {
    return null;
  }
}
